/**
 * Select Order Modal Component
 *
 * Modal to select a purchase order for creating a goods receipt.
 * Shows only orders with status SENT or PARTIALLY_RECEIVED.
 */

import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Modal, ModalFooter } from '@/components/ui';
import { Input } from '@/components/ui';
import { Button } from '@/components/ui';
import { Badge } from '@/components/ui';
import { Skeleton } from '@/components/ui';
import { EmptyState } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useReceivableOrders } from '../hooks/useReceivableOrders';
import { PurchaseOrderStatus, PURCHASE_ORDER_STATUS_LABELS } from '@bstock/shared';
import type { PurchaseOrderListItem } from '@bstock/shared';

interface SelectOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (orderId: string) => void;
}

/**
 * Get badge variant from order status
 */
function getStatusBadgeVariant(status: PurchaseOrderStatus): 'primary' | 'warning' {
  switch (status) {
    case PurchaseOrderStatus.SENT:
      return 'primary';
    case PurchaseOrderStatus.PARTIALLY_RECEIVED:
      return 'warning';
    default:
      return 'primary';
  }
}

export function SelectOrderModal({ isOpen, onClose, onSelect }: SelectOrderModalProps) {
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const [search, setSearch] = useState('');

  // Fetch receivable orders only when modal is open
  const { data: orders, isLoading } = useReceivableOrders(isOpen);

  // Filter orders locally based on search
  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    if (!search.trim()) return orders;

    const searchLower = search.toLowerCase().trim();
    return orders.filter(
      (order) =>
        order.orderNumber.toLowerCase().includes(searchLower) ||
        order.supplier.name.toLowerCase().includes(searchLower)
    );
  }, [orders, search]);

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  const handleSelect = (order: PurchaseOrderListItem) => {
    setSearch('');
    onSelect(order.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Selectionner une commande"
      size="2xl"
    >
      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par n° ou fournisseur..."
            className="pl-10"
          />
        </div>

        {/* Orders list */}
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-neutral-50 border-b border-neutral-200 text-sm font-medium text-neutral-600">
            <div className="col-span-3">N° Commande</div>
            <div className="col-span-3">Fournisseur</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2 text-right">Montant HT</div>
            <div className="col-span-2 text-center">Statut</div>
          </div>

          {/* Table body */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="p-8">
                <EmptyState
                  title={search ? 'Aucune commande trouvee' : 'Aucune commande disponible'}
                  description={
                    search
                      ? 'Aucune commande ne correspond a votre recherche'
                      : 'Il n\'y a pas de commande en attente de reception'
                  }
                />
              </div>
            ) : (
              filteredOrders.map((order) => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => handleSelect(order)}
                  className="w-full grid grid-cols-12 gap-4 px-4 py-3 text-sm hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 transition-colors text-left"
                >
                  <div className="col-span-3 font-medium text-dark-500">
                    {order.orderNumber}
                  </div>
                  <div className="col-span-3 text-neutral-700 truncate">
                    {order.supplier.name}
                  </div>
                  <div className="col-span-2 text-neutral-600">
                    {formatDateShort(order.orderDate)}
                  </div>
                  <div className="col-span-2 text-right text-neutral-700">
                    {formatCentimes(order.totalExclTax, currency)}
                  </div>
                  <div className="col-span-2 text-center">
                    <Badge variant={getStatusBadgeVariant(order.status)}>
                      {PURCHASE_ORDER_STATUS_LABELS[order.status]}
                    </Badge>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Annuler
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
